// React
import React, { useMemo } from 'react'
import PropTypes from 'prop-types'

// Libraries
import { clsx } from 'clsx'

// Components
import BOrderPicker from 'ui/blocks/Picker/types/order'
import BPicker from 'ui/blocks/Picker'
import Icon from 'ui/components/Icon'
import DateTimePicker from 'ui/components/Picker/DateTimePicker'
import InputField from 'ui/components/InputField'

// Hooks
import { useDurations, usePicker } from 'ui/components/Picker/hooks'
import { useOrm } from 'shared/utils/useOrm'
import { useSettings } from 'shared/utils/useSettings'
import { useFormikContext } from 'formik'

// Shared
import { Field } from 'shared/form'
import { useTranslation } from 'shared/utils/useTranslation'
import { parseFixedDefaultTime } from 'shared/utils/time'
import { defaultDefaultTimes } from 'ui/components/Picker/utils'

export const Durations = ({ onChange: _onChange, onOpen, ...props }) => {
  const picker = usePicker()

  const { t } = useTranslation()
  const form = useFormikContext()

  const durations = useOrm('operating_rules', (rules) => {
    return rules.getAll({ data_type: 'timeslot_duration' })
  })
  const startDefaultTimeSetting = useSettings('orders.start_fixed_at', { default: defaultDefaultTimes.start })
  const stopDefaultTimeSetting = useSettings('orders.stop_fixed_at', { default: defaultDefaultTimes.stop })

  const { clearCacheRef, duration, durationObject, onChange, getRange } = useDurations(props.onDurationChange)

  const from = form.values.starts_at ? new Date(form.values.starts_at) : null
  const to = form.values.stops_at ? new Date(form.values.stops_at) : null

  const options = useMemo(() => {
    return durations
      .sortBy((duration) => duration.data.length)
      .map((duration) => {
        const isPlural = duration.data.duration > 1

        return {
          value: duration.id,
          label: t('common.periods.count', {
            count: duration.data.duration,
            period: t(`common.periods.${duration.data.period}${isPlural ? 's' : ''}`)
          })
        }
      })
      .toJS()
  }, [durations])

  const isReturnTimeDisabled = durationObject?.data?.period === 'hour'
  const isDelivery = props.fulfillmentType === 'delivery'

  const getBlockedDays = (date) => {
    const durationData = durationObject?.data
    const defaultTimes = {
      start: parseFixedDefaultTime(startDefaultTimeSetting),
      stop: parseFixedDefaultTime(stopDefaultTimeSetting)
    }

    return !picker.isDaySelectable({ type: 'start', date, durationData, defaultTimes })
  }

  const handleChangeStartsAt = (date) => {
    const range = getRange(date, form.values, durationObject)

    form.setValues({
      ...form.values,
      starts_at: range.from.toJSON(),
      stops_at: range.to.toJSON(),
      charge_in_seconds: parseInt(duration)
    })
  }

  const handleBlurStartsAt = (date) => {
    if (getBlockedDays(date)) {
      date = picker.setDefaultTime({ type: 'start', forceDefaultTime: true })

      handleChangeStartsAt(date)
    }
  }

  const handleSelectDuration = (durationId) => {
    const durationObject = durations.toArray().find((duration) => duration.id === durationId)
    const duration = durationObject?.data.length

    form.setValues({
      ...form.values,
      stops_at: null
    })

    onChange({
      duration,
      durationObject,
      changePeriod: true
    })

    picker.onChange('duration')
  }

  return (
    <BOrderPicker.Durations className={clsx({ 'no-times': !picker.useTimes })} disabled={props.disabled}>
      <BPicker.Field>
        <BPicker.Label>{t('common.duration')}</BPicker.Label>
        <InputField
          {...props}
          field={{
            value: durationObject?.id,
            onChange: handleSelectDuration
          }}
          type="durations"
          options={options}
          innerRef={(ref) => {
            picker.refs.durationsRef.current = ref
          }}
          data-tid="Durations"
          placeholder={t('common.duration')}
          modifiers="display-inline-flex"
          hideLabel
        />
      </BPicker.Field>
      <BOrderPicker.Period className={clsx({ 'no-times': !picker.useTimes })} modifiers="width-100p" disableStyling>
        <BPicker.Field>
          <BPicker.Label>{isDelivery ? t('common.from') : t('common.start.present')}</BPicker.Label>
          <Field
            {...props}
            component={DateTimePicker}
            name="starts_at"
            onChange={handleChangeStartsAt}
            onBlur={handleBlurStartsAt}
            isTimeSelectable={(timestring) => {
              return picker.isTimeSelectable({
                type: 'start',
                timestring,
                durationData: durationObject?.data,
                allowPast: picker.allowPast
              })
            }}
            blockedDays={getBlockedDays}
            inputDateRef={picker.refs.fromDateRef}
            inputTimeRef={picker.refs.fromTimeRef}
            from={from}
            to={to}
            mode="from"
            selection={({ hover }) => getRange(hover, form.values, durationObject)}
            data-tid="Starts at picker"
            onMonthChange={props.onMonthChange?.bind(null, { type: 'start' })}
            onOpen={onOpen?.bind(null, { type: 'start', values: form.values })}
            duration={duration}
            disabledDate={!durationObject}
            disabledTime={!durationObject}
            clearCacheRef={(ref) => {
              clearCacheRef.current = ref
            }}
            withAvailability
            hideLabel
            initialError
          />
        </BPicker.Field>
        <BPicker.Decorator>
          <Icon icon="arrow-right" />
        </BPicker.Decorator>
        <BPicker.Field>
          <BPicker.Label>{isDelivery ? t('common.until') : t('common.stop.present')}</BPicker.Label>
          <Field
            {...props}
            component={DateTimePicker}
            name="stops_at"
            isTimeSelectable={(timestring) => {
              return picker.isTimeSelectable({
                type: 'stop',
                timestring,
                allowPast: picker.allowPast
              })
            }}
            from={from}
            to={to}
            inputDateRef={picker.refs.tillDateRef}
            inputTimeRef={picker.refs.tillTimeRef}
            mode="to"
            data-tid="Stops at picker"
            disabledTime={isReturnTimeDisabled || !durationObject}
            onOpen={onOpen?.bind(null, { type: 'stop', values: form.values })}
            disabledDate
            withAvailability
            hideLabel
            initialError
          />
        </BPicker.Field>
      </BOrderPicker.Period>
    </BOrderPicker.Durations>
  )
}

Durations.propTypes = {
  fulfillmentType: PropTypes.oneOf(['pickup', 'delivery']),
  onDurationChange: PropTypes.func,
  disabled: PropTypes.bool,
  onMonthChange: PropTypes.func,
  onChange: PropTypes.func,
  onOpen: PropTypes.func
}
